import { ITable } from '@/types/Table';
import * as ExcelJS from 'exceljs';
import { v4 as uuidv4 } from 'uuid';

/**
 * Generates a new object with all the non-empty fields from the input object.
 * @param {T} obj - The input object.
 * @return {Partial<T>} A new object with only the non-empty fields.
 */
function getWithoutEmptyFields<T>(obj: Partial<T>): Partial<T> {
  const result: Partial<T> = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      result[key as keyof T] = value as T[keyof T];
    }
  });
  return result;
}

/**
 * Go to an absolute URL in the same window, unless this document is inside an iframe
 * (e.g. /embed on a third-party site). Then open a new tab so the address bar shows our app,
 * not the parent page URL.
 */
/**
 * Allows only same-origin relative paths for post-login redirects (blocks //evil.com and https:).
 */
export function getSafeInternalReturnPath(raw: string | null | undefined): string | null {
  if (raw == null || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return null;
  if (trimmed.includes('://')) return null;
  const noHash = trimmed.split('#')[0];
  return noHash || null;
}

export function navigateSameWindowOrOpenTab(url: string): void {
  if (typeof window === 'undefined') return;
  const isFramed = window.self !== window.top;
  if (isFramed) {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    window.location.assign(url);
  }
}

export function copyToClipboard(link: string): Promise<void> {
  // Try modern clipboard API first
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard
      .writeText(link)
      .catch((error) => {
        return fallbackCopyToClipboard(link);
      });
  }
  
  // Fallback for older browsers or restricted contexts
  return fallbackCopyToClipboard(link);
}

function fallbackCopyToClipboard(text: string): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        resolve();
      } else {
        reject(new Error('Fallback copy command failed'));
      }
    } catch (error) {
      reject(error);
    }
  });
}

export function reducePrecision(input: string): string {
  return input.replace(/-?\d*\.\d+/g, (match) => {
    return parseFloat(match).toFixed(3);
  });
}

const findPNetIdColumnIndex = (headerRow: ExcelJS.Row): number => {
  let pNetIdColumnIndex = -1;
  headerRow.eachCell((cell, colNumber) => {
    const value = cell.value?.toString().toLowerCase() || '';
    // Look for columns named exactly pNetId or containing both "pnet" and "id"
    if (value === 'pnetid' || (value.includes('pnet') && value.includes('id'))) {
      pNetIdColumnIndex = colNumber;
    }
  });
  return pNetIdColumnIndex;
};

export const convertExcelToTable = async (
  file: File,
  includeDate: boolean = false
): Promise<ITable> => {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());

  const worksheet = workbook.worksheets[0]; // Get first worksheet
  if (!worksheet) {
    throw new Error('No worksheet found in Excel file');
  }

  const table: ITable = {
    _id: '', // This will be set by the server
    name: file.name, // worksheet.name || file.name.replace('.xlsx', ''),
    columns: {},
    rows: {},
  };

  if (includeDate) {
    // Format date as DD-MM-YYYY using toLocaleDateString
    table.name =
      new Date()
        .toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
        .replace(/\//g, '-') +
      '_' +
      table.name;
  }

  // Process header row to create columns
  const headerRow = worksheet.getRow(1);
  headerRow.eachCell((cell: ExcelJS.Cell, colNumber: number) => {
    const columnName = cell.value?.toString() || `Column${colNumber}`;
    table.columns[columnName] = {
      name: columnName,
      properties: {}, // Add any additional column properties here if needed
    };
  });

  // Find pNetId column if it exists
  const pNetIdColumnIndex = findPNetIdColumnIndex(headerRow);

  // Process data rows
  for (let rowNumber = 2; rowNumber <= worksheet.rowCount; rowNumber++) {
    const excelRow = worksheet.getRow(rowNumber);
    const rowData: { [key: string]: string } = {};

    // Get pNetId from the designated column or generate a UUID
    let pNetId: string;
    if (pNetIdColumnIndex > 0) {
      const pNetIdCell = excelRow.getCell(pNetIdColumnIndex);
      pNetId = pNetIdCell.value?.toString() || uuidv4();
    } else {
      pNetId = uuidv4();
    }

    excelRow.eachCell((cell: ExcelJS.Cell, colNumber: number) => {
      const columnName = headerRow.getCell(colNumber).value?.toString() || `Column${colNumber}`;
      rowData[columnName] = cell.value?.toString() || '';
    });

    table.rows[pNetId] = {
      pNetId,
      data: rowData,
    };
  }

  return table;
};

/**
 * Detects AR capable devices using feature detection (no hardcoded device names)
 * Uses CSS Media Queries for reliable device type detection
 * This approach works correctly even in responsive test mode
 * @returns Object with device detection information
 */
export const detectARCapableDevice = () => {
  // WebXR API presence ≠ headset connected.
  // On HTTPS (production), Chrome/Edge expose navigator.xr on normal desktops too.
  // On plain HTTP LAN/dev hosts it is often missing (not a secure context) — that used to
  // make isDesktop true in dev and false in prod, hiding the QR/AR control on live.
  const hasWebXR = 'xr' in navigator;

  // Priority: CSS Media Queries for reliable device type detection
  // These queries check actual device capabilities, not screen size
  // Works correctly even in responsive test mode (Chrome DevTools)

  // pointer: coarse = touch device (finger, stylus) - primary input is touch
  // pointer: fine = precise pointing device (mouse, trackpad, VR controller)
  const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;

  // Additional touch detection for extra reliability
  const hasTouchEvents = 'ontouchstart' in window;
  const hasMultipleTouchPoints = navigator.maxTouchPoints && navigator.maxTouchPoints > 1;

  // Primary detection: CSS Media Query (works in responsive test mode)
  // pointer: coarse means the primary input is touch-based (finger/stylus)
  const isTouchDevice = Boolean(hasCoarsePointer || hasMultipleTouchPoints);

  // Fallback: If media queries not fully supported, use touch events
  // But require multiple touch points to avoid MacBook trackpad false positives
  // MacBook trackpads report maxTouchPoints = 1, real touch screens = 2+
  const hasTouchSupport = Boolean(isTouchDevice || (hasTouchEvents && hasMultipleTouchPoints));

  // Feature detection: Does the browser support AR/VR sessions?
  const canUseImmersiveAR = Boolean(
    hasWebXR && navigator.xr && 'isSessionSupported' in navigator.xr
  );

  /**
   * IMPORTANT: isDesktop is based on DEVICE INPUT (touch vs pointer), NOT WebXR API presence
   * and NOT CSS viewport width.
   * - Desktop = no touch primary input (mouse/trackpad)
   * - Do NOT use `'xr' in navigator` here — that is true on HTTPS desktop browsers
   */
  const isDesktop = Boolean(!hasTouchSupport);
  const isMobile = Boolean(hasTouchSupport);
  // True VR headset detection needs async isSessionSupported('immersive-vr'); keep false sync.
  const isVR = false;

  // Phone/tablet AR, or browser that exposes WebXR (session support checked separately)
  const isARCapable = Boolean(hasTouchSupport || canUseImmersiveAR);

  return {
    isARCapable,
    hasWebXR,
    isDesktop,
    isMobile,
    isVR,
    hasTouchSupport,
    canUseImmersiveAR,
  };
};
